import { apiFetch } from "@/lib/api";

/**
 * The Django API wraps list bodies as `{ success, message, data, meta }`
 * (`APIJSONRenderer`). We only need `data` here.
 */
interface Envelope<T> {
  data: T;
  meta?: PractitionerListMeta;
}

interface ApiPractitioner {
  id: string;
  full_name: string;
}

/** A practitioner option for assignment dropdowns. */
export interface PractitionerOption {
  id: string;
  name: string;
}

// Cache in-flight/resolved requests *per firm* so repeated mounts (and
// StrictMode's dev double-invoke) share one network call. Keyed by firm id: a
// GA/SA or house CD acting on behalf of a Business Practitioner must not be
// served the house firm's cached list (or vice-versa). Each firm's list is small
// and rarely changes within a session; call clearPractitionersCache() after a
// create/delete to force a refresh.
const cache = new Map<string, Promise<PractitionerOption[]>>();

/**
 * GET /practitioners/ — practitioners for assignment selects. Pass `firm` to
 * scope to one accessible firm (a GA/SA or house CD acting on behalf of a BP);
 * omit it for the caller's own firm. Pass `includeHouse` alongside `firm` to also
 * include the house CAP practitioners (NSI signature authority) — used when
 * assigning a practitioner to a Business Practitioner's client.
 */
export function listPractitioners(
  firm?: string,
  force = false,
  includeHouse = false,
): Promise<PractitionerOption[]> {
  const key = `${firm ?? "__own__"}${includeHouse ? ":+house" : ""}`;
  if (force) cache.delete(key);
  let pending = cache.get(key);
  if (!pending) {
    const query = firm
      ? `/practitioners/?page_size=100&firm=${encodeURIComponent(firm)}${includeHouse ? "&include_house=1" : ""}`
      : "/practitioners/?page_size=100";
    pending = apiFetch<Envelope<ApiPractitioner[]>>(query)
      .then((body) => body.data.map((p) => ({ id: p.id, name: p.full_name })))
      .catch((err) => {
        cache.delete(key); // don't cache a failure — let the next call retry
        throw err;
      });
    cache.set(key, pending);
  }
  return pending;
}

/** Drop every firm's cached practitioners so the next call refetches. */
export function clearPractitionersCache() {
  cache.clear();
}

// --- Full management (Settings → Practitioners) --------------------------

// The 11 IRS Form 2848 Part II designation codes (stored as their letter).
export type PractitionerDesignation =
  | "a" | "b" | "c" | "d" | "e" | "f" | "g" | "h" | "i" | "k" | "r";

export const DESIGNATION_OPTIONS: { value: PractitionerDesignation; label: string }[] = [
  { value: "a", label: "Attorney" },
  { value: "b", label: "Certified Public Accountant" },
  { value: "c", label: "Enrolled Agent" },
  { value: "d", label: "Officer" },
  { value: "e", label: "Full-Time Employee" },
  { value: "f", label: "Family Member" },
  { value: "g", label: "Enrolled Actuary" },
  { value: "h", label: "Unenrolled Return Preparer" },
  { value: "i", label: "Registered Tax Return Preparer" },
  { value: "k", label: "Student Attorney or CPA" },
  { value: "r", label: "Enrolled Retirement Plan Agent" },
];

export type DefaultDesignation = "NONE" | "PRIMARY" | "SECONDARY";

export interface Practitioner {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  fax: string;
  cell: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  firmName: string;
  firmEin: string;
  designation: PractitionerDesignation;
  ptin: string;
  cafNumber: string;
  /** Whether an EFIN is on file (the value itself is write-only, never returned). */
  hasEfin: boolean;
  ctec: string;
  nyptrin: string;
  licensing: string;
  licenseNumber: string;
  businessCaf: boolean;
  esrvUserId: string;
  aboutText: string;
  defaultDesignation: DefaultDesignation;
  linkedUserCount: number;
}

export interface PractitionerPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  fax: string;
  cell: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  firmName: string;
  firmEin: string;
  designation: PractitionerDesignation;
  ptin: string;
  cafNumber: string;
  /** Write-only; omit (undefined) on edit to keep the stored EFIN unchanged. */
  efin?: string;
  ctec: string;
  nyptrin: string;
  licensing: string;
  licenseNumber: string;
  businessCaf: boolean;
  esrvUserId: string;
  aboutText: string;
}

interface ApiPractitionerDetail {
  id: string;
  full_name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  fax: string;
  cell: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
  firm_name: string;
  firm_ein: string;
  designation: PractitionerDesignation;
  ptin: string;
  caf_number: string;
  has_efin: boolean;
  ctec: string;
  nyptrin: string;
  licensing: string;
  license_number: string;
  business_caf: boolean;
  esrv_user_id: string;
  about_text: string;
  default_designation: DefaultDesignation;
  linked_user_count: number;
}

function toPractitioner(p: ApiPractitionerDetail): Practitioner {
  return {
    id: p.id,
    fullName: p.full_name,
    firstName: p.first_name,
    lastName: p.last_name,
    email: p.email,
    phone: p.phone,
    fax: p.fax,
    cell: p.cell,
    addressLine1: p.address_line1,
    addressLine2: p.address_line2,
    city: p.city,
    state: p.state,
    zipCode: p.zip_code,
    firmName: p.firm_name,
    firmEin: p.firm_ein,
    designation: p.designation,
    ptin: p.ptin,
    cafNumber: p.caf_number,
    hasEfin: p.has_efin ?? false,
    ctec: p.ctec,
    nyptrin: p.nyptrin,
    licensing: p.licensing,
    licenseNumber: p.license_number,
    businessCaf: p.business_caf,
    esrvUserId: p.esrv_user_id,
    aboutText: p.about_text,
    defaultDesignation: p.default_designation,
    linkedUserCount: p.linked_user_count ?? 0,
  };
}

function toBody(payload: PractitionerPayload) {
  return {
    first_name: payload.firstName,
    last_name: payload.lastName,
    email: payload.email,
    phone: payload.phone,
    fax: payload.fax,
    cell: payload.cell,
    address_line1: payload.addressLine1,
    address_line2: payload.addressLine2,
    city: payload.city,
    state: payload.state,
    zip_code: payload.zipCode,
    firm_name: payload.firmName,
    firm_ein: payload.firmEin,
    designation: payload.designation,
    ptin: payload.ptin,
    caf_number: payload.cafNumber,
    efin: payload.efin,
    ctec: payload.ctec,
    nyptrin: payload.nyptrin,
    licensing: payload.licensing,
    license_number: payload.licenseNumber,
    business_caf: payload.businessCaf,
    esrv_user_id: payload.esrvUserId,
    about_text: payload.aboutText,
  };
}

export interface PractitionerListMeta {
  count: number;
  next: string | null;
  previous: string | null;
}

export interface PractitionerListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  designation?: PractitionerDesignation;
  default?: DefaultDesignation;
  a2a?: "connected" | "none";
  /** DRF ordering value, e.g. "last_name" or "-linked_user_count". */
  ordering?: string;
  /** Scope to one accessible firm (a house user acting on behalf of a BP). */
  firm?: string;
}

export interface PractitionerListResult {
  practitioners: Practitioner[];
  meta: PractitionerListMeta;
}

function buildQuery(params: PractitionerListParams): string {
  const q = new URLSearchParams();
  if (params.page) q.set("page", String(params.page));
  if (params.pageSize) q.set("page_size", String(params.pageSize));
  if (params.search) q.set("search", params.search);
  if (params.designation) q.set("designation", params.designation);
  if (params.default) q.set("default", params.default);
  if (params.a2a) q.set("a2a", params.a2a);
  if (params.ordering) q.set("ordering", params.ordering);
  if (params.firm) q.set("firm", params.firm);
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** GET /practitioners/ — a filtered, paginated page for the management table. */
export async function listPractitionersPage(
  params: PractitionerListParams = {},
): Promise<PractitionerListResult> {
  const body = await apiFetch<Envelope<ApiPractitionerDetail[]>>(
    `/practitioners/${buildQuery(params)}`,
  );
  return {
    practitioners: body.data.map(toPractitioner),
    meta: body.meta ?? { count: body.data.length, next: null, previous: null },
  };
}

export async function createPractitioner(payload: PractitionerPayload): Promise<Practitioner> {
  const body = await apiFetch<Envelope<ApiPractitionerDetail>>("/practitioners/", {
    method: "POST",
    json: toBody(payload),
  });
  clearPractitionersCache();
  return toPractitioner(body.data);
}

export async function updatePractitioner(
  id: string,
  payload: PractitionerPayload,
): Promise<Practitioner> {
  const body = await apiFetch<Envelope<ApiPractitionerDetail>>(`/practitioners/${id}/`, {
    method: "PATCH",
    json: toBody(payload),
  });
  clearPractitionersCache();
  return toPractitioner(body.data);
}

export async function deletePractitioner(id: string): Promise<void> {
  await apiFetch<void>(`/practitioners/${id}/`, { method: "DELETE" });
  clearPractitionersCache();
}

/** POST /practitioners/:id/set-default/ — make this the firm's primary. */
export async function setDefaultPractitioner(id: string): Promise<Practitioner> {
  const body = await apiFetch<Envelope<ApiPractitionerDetail>>(
    `/practitioners/${id}/set-default/`,
    { method: "POST" },
  );
  clearPractitionersCache();
  return toPractitioner(body.data);
}

/** POST /practitioners/:id/set-secondary/ — make this the firm's secondary. */
export async function setSecondaryPractitioner(id: string): Promise<Practitioner> {
  const body = await apiFetch<Envelope<ApiPractitionerDetail>>(
    `/practitioners/${id}/set-secondary/`,
    { method: "POST" },
  );
  clearPractitionersCache();
  return toPractitioner(body.data);
}

/** PATCH — clear the A2A user ID (also unbinds any login defaulting to it). */
export async function clearPractitionerA2A(id: string): Promise<Practitioner> {
  const body = await apiFetch<Envelope<ApiPractitionerDetail>>(`/practitioners/${id}/`, {
    method: "PATCH",
    json: { esrv_user_id: "" },
  });
  clearPractitionersCache();
  return toPractitioner(body.data);
}

/** POST /practitioners/:id/link/ — bind this practitioner's A2A to my login. */
export async function linkPractitionerToMe(id: string): Promise<Practitioner> {
  const body = await apiFetch<Envelope<ApiPractitionerDetail>>(
    `/practitioners/${id}/link/`,
    { method: "POST" },
  );
  clearPractitionersCache();
  return toPractitioner(body.data);
}

export interface AssignedClient {
  id: string;
  displayName: string;
  clientType: string;
  irsStatus: string;
  status: string;
}

interface ApiAssignedClient {
  id: string;
  display_name: string;
  client_type: string;
  irs_status: string;
  status: string;
}

function toAssignedClient(c: ApiAssignedClient): AssignedClient {
  return {
    id: c.id,
    displayName: c.display_name,
    clientType: c.client_type,
    irsStatus: c.irs_status,
    status: c.status,
  };
}

/** GET /practitioners/:id/clients/ — live clients assigned (delete guard). */
export async function listPractitionerClients(id: string): Promise<AssignedClient[]> {
  const body = await apiFetch<Envelope<{ count: number; clients: ApiAssignedClient[] }>>(
    `/practitioners/${id}/clients/`,
  );
  return body.data.clients.map(toAssignedClient);
}

/**
 * POST /practitioners/:id/reassign/ — move this practitioner's clients to
 * another, then soft-delete it. `reassignTo` may be null when unassigned.
 */
export async function reassignPractitioner(
  id: string,
  reassignTo: string | null,
): Promise<void> {
  await apiFetch<void>(`/practitioners/${id}/reassign/`, {
    method: "POST",
    json: reassignTo ? { reassign_to: reassignTo } : {},
  });
  clearPractitionersCache();
}

export interface AllocationRow {
  id: string;
  fullName: string;
  designation: PractitionerDesignation;
  defaultDesignation: DefaultDesignation;
  clients: AssignedClient[];
}

interface ApiAllocationRow {
  id: string;
  full_name: string;
  designation: PractitionerDesignation;
  default_designation: DefaultDesignation;
  clients: ApiAssignedClient[];
}

/** GET /practitioners/allocation/ — each practitioner and their clients. */
export async function listPractitionerAllocation(firm?: string): Promise<AllocationRow[]> {
  const firmQ = firm ? `&firm=${encodeURIComponent(firm)}` : "";
  const body = await apiFetch<Envelope<ApiAllocationRow[]>>(
    `/practitioners/allocation/?page_size=100${firmQ}`,
  );
  return body.data.map((r) => ({
    id: r.id,
    fullName: r.full_name,
    designation: r.designation,
    defaultDesignation: r.default_designation,
    clients: r.clients.map(toAssignedClient),
  }));
}
